import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PageHeaderPrimary, NonReturnableIcon } from 'cat-ecommerce-alloy';
import PropTypes from 'prop-types';
import ContactUsOptions from '../ContactUsOptions';
import {
  replaceTokensWithComponents,
  replaceTokensInString,
  decodeHTML
} from '@app/utils';
import Conditional from '../Conditional';
import { useECT } from '@app/hooks';
import {
  DEALER_RETURN_POLICY_ESPOT_NAME,
  PCC_RETURN_POLICY_MESSAGE_TYPE,
  RETURN_POLICY_LINK_ESPOT_NAME,
  PCC_NON_RETURNABLE_PART_NOTE
} from './constants';
import PurifiedHtml from '@app/components/common/PurifiedHtml';
import ErrorWarning from '@app/components/common/Blocks/ErrorWarning';
import { STATUS } from '@app/constants/commonConstants';

const ReturnPolicyModalContent = ({
  orderId = '',
  showContactUs = true,
  openDealerLocator
}) => {
  const [t] = useTranslation();

  const isCatCSR = useSelector(s => s.common.isCatCSR);

  const textClassName = 'u-fs--16 u-color--DarkGray mb-4';

  const returnPolicyWithIcon = replaceTokensWithComponents(
    decodeHTML(t('NON_RETURNABLE_PART_STATEMENT')),
    <NonReturnableIcon className={'u-fill--DarkRed'} width={16} height={16} />
  );

  const dealerName = useSelector(s => s.dealer?.dealerName);
  const dealerReturnPolicyTitle = replaceTokensInString(
    t('RETURN_POLICY_DEALER'),
    dealerName
  );

  const { data: dealerReturnPolicyECT, error: hasEctError } = useECT(
    PCC_RETURN_POLICY_MESSAGE_TYPE,
    DEALER_RETURN_POLICY_ESPOT_NAME
  );
  const dealerReturnPolicy =
    dealerReturnPolicyECT?.text?.length > 0 ? dealerReturnPolicyECT?.text : '';

  const ectStatus = useSelector(state => state.ect?.ectMessageStatus);

  const { data: dealerReturnPolicyLinkECT } = useECT(
    PCC_NON_RETURNABLE_PART_NOTE,
    RETURN_POLICY_LINK_ESPOT_NAME
  );

  const dealerReturnPolicyLink =
    dealerReturnPolicyLinkECT?.text?.length > 0
      ? dealerReturnPolicyLinkECT?.text
      : '';
  const dealerReturnPolicyLinkURL =
    dealerReturnPolicyLinkECT?.url?.length > 0
      ? dealerReturnPolicyLinkECT?.url
      : '';
  const ectErrorTitle = replaceTokensInString(
    t('ECT_CONN_ABORTED_DEALER_TITLE'),
    dealerName
  );

  return (
    <div className="mt-3">
      <PageHeaderPrimary title={t('RETURN_POLICY_INFO')} />
      <p className={`${textClassName} mt-4`}>{returnPolicyWithIcon}</p>
      <Conditional test={!!hasEctError && ectStatus === STATUS.REJECTED}>
        <div className={`pt-md-2 pt-sm-0 mb-2`} data-testid="ect-error">
          <ErrorWarning
            errorTitle={ectErrorTitle}
            showContactUsButton={false}
            errorMsg={'ECT_CONN_ABORTED_DEALER_DESC'}
            errorMsgParams={[dealerName]}
            dismissible
          />
        </div>
      </Conditional>
      <Conditional test={!!dealerReturnPolicy}>
        <h3 className="h2 mb-4 display-2">{dealerReturnPolicyTitle}</h3>
        <PurifiedHtml
          className={textClassName}
          htmlString={dealerReturnPolicy}
          data-testid="modelDealerReturnPolicy"
        />
      </Conditional>
      <Conditional
        test={!!dealerReturnPolicyLink || !!dealerReturnPolicyLinkURL}
      >
        <PurifiedHtml
          href={dealerReturnPolicyLinkURL}
          target={dealerReturnPolicyLinkURL ? '_blank' : ''}
          className="text-center"
          htmlString={dealerReturnPolicyLink}
          data-testid="modelDealerReturnPolicyLink"
          as="a"
        />
      </Conditional>
      <Conditional test={showContactUs}>
        <ContactUsOptions
          orderId={orderId}
          isCSR={isCatCSR}
          openDealerLocator={openDealerLocator}
        />
      </Conditional>
    </div>
  );
};

ReturnPolicyModalContent.propTypes = {
  orderId: PropTypes.string,
  showContactUs: PropTypes.bool,
  openDealerLocator: PropTypes.func
};

export default ReturnPolicyModalContent;
